import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { AlertCircle, Clock, Shield, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function PendingUser() {
  const { t } = useTranslation('pendingUser');
  const { logout, checkAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [userData, setUserData] = useState(location.state?.user || null);
  const [isChecking, setIsChecking] = useState(false);
  const [lastChecked, setLastChecked] = useState(null);

  // Function to check if user has been approved
  const checkApprovalStatus = useCallback(async () => {
    setIsChecking(true);
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true
      });
      
      if (response.status === 200) {
        const freshData = response.data;
        setUserData(freshData);
        setLastChecked(new Date());
        
        // Check if user can now access dashboard
        if (freshData.can_access_dashboard === true) {
          // User has been approved! Refresh auth context and redirect
          await checkAuth();
          navigate('/dashboard', { replace: true });
          return;
        }
      }
    } catch (error) {
      console.error('Failed to check approval status:', error);
      // If auth fails, redirect to login
      if (error.response?.status === 401) {
        navigate('/login', { replace: true });
      }
    } finally {
      setIsChecking(false);
    }
  }, [checkAuth, navigate]);

  // Check on initial load
  useEffect(() => {
    checkApprovalStatus();
  }, [checkApprovalStatus]);

  // Auto-check every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      checkApprovalStatus();
    }, 10000); // Check every 10 seconds

    return () => clearInterval(interval);
  }, [checkApprovalStatus]);

  // Determine what's missing for the user
  const getMissingRequirements = () => {
    const missing = [];
    if (!userData) return missing;
    
    if (userData.role === 'pending') {
      missing.push('missing.rolePending');
    }
    if (!userData.is_approved) {
      missing.push('missing.approval');
    }
    if (!userData.has_project_access && userData.role !== 'super_admin' && userData.role !== 'admin') {
      missing.push('missing.projectAccess');
    }
    return missing;
  };

  const missingRequirements = getMissingRequirements();

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900 px-4">
      <div className="max-w-md w-full bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 p-8">
        <div className="flex justify-center mb-6">
          <div className="relative">
            <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-full">
              <Clock className="h-16 w-16 text-amber-500" />
            </div>
            <div className="absolute -top-1 -right-1 p-1.5 bg-emerald-500 rounded-full">
              <Shield className="h-4 w-4 text-white" />
            </div>
          </div>
        </div>
        
        <h2 className="text-2xl font-semibold text-slate-900 dark:text-slate-100 mb-4 text-center" style={{ fontFamily: "'Outfit', -apple-system, 'Segoe UI', Roboto, sans-serif" }}>
          {t('title')}
        </h2>
        
        <div className="space-y-4 mb-6">
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-amber-900 dark:text-amber-100 mb-1">
                  {t('notice.title')}
                </p>
                <p className="text-sm text-amber-700 dark:text-amber-300 mb-2">
                  {t('notice.required')}
                </p>
                <ul className="text-sm text-amber-700 dark:text-amber-300 list-disc list-inside space-y-1">
                  {missingRequirements.length > 0 ? (
                    missingRequirements.map((req, idx) => (
                      <li key={idx}>{t(req)}</li>
                    ))
                  ) : (
                    <>
                      <li>{t('defaults.approval')}</li>
                      <li>{t('defaults.role')}</li>
                      <li>{t('defaults.projectAccess')}</li>
                    </>
                  )}
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-slate-50 dark:bg-slate-900/50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-slate-600 dark:text-slate-400">{t('info.email')}</span>
              <span className="font-medium text-slate-900 dark:text-slate-100">{userData?.email || t('info.na')}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-600 dark:text-slate-400">{t('info.role')}</span>
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                userData?.role === 'pending' 
                  ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                  : 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
              }`}>
                {userData?.role ? t(`common:roles.${userData.role}`, { defaultValue: userData.role }) : t('common:roles.pending')}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-600 dark:text-slate-400">{t('info.approved')}</span>
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                userData?.is_approved 
                  ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
                  : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
              }`}>
                {userData?.is_approved ? t('common:actions.yes') : t('common:actions.no')}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-600 dark:text-slate-400">{t('info.projectAccess')}</span>
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                userData?.has_project_access 
                  ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
                  : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
              }`}>
                {userData?.has_project_access ? t('common:actions.yes') : t('common:actions.no')}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-600 dark:text-slate-400">{t('info.registered')}</span>
              <span className="text-slate-700 dark:text-slate-300">
                {userData?.created_at ? new Date(userData.created_at).toLocaleDateString() : t('info.na')}
              </span>
            </div>
          </div>

          {/* Auto-refresh indicator */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <span className="text-xs text-blue-700 dark:text-blue-300">
                {isChecking ? t('autoCheck.checking') : t('autoCheck.interval')}
              </span>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={checkApprovalStatus}
                disabled={isChecking}
                className="h-8 px-2"
                data-testid="refresh-status-btn"
              >
                <RefreshCw className={`h-4 w-4 ${isChecking ? 'animate-spin' : ''}`} />
              </Button>
            </div>
            {lastChecked && (
              <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                {t('autoCheck.lastChecked', { time: lastChecked.toLocaleTimeString() })}
              </p>
            )}
          </div>
        </div>
        
        <p className="text-center text-sm text-slate-600 dark:text-slate-400 mb-6">
          {t('autoUpdate')}
        </p>
        
        <Button onClick={logout} variant="outline" className="w-full" data-testid="pending-logout-btn">
          {t('common:layout.logout')}
        </Button>
      </div>
    </div>
  );
}

export default PendingUser;
